import { useMemo, useRef, useState } from "react";
import qrcode from "qrcode-generator";
import { SectionCard } from "./SectionCard";
import { buildContactShare } from "../lib/contactShare";

// "Share contact" card on a MeshCore node's page (issue #35): the QR code and
// link the MeshCore app uses to add a contact. Built only from the advertised
// name, public key and node type we already store.

const QUIET_ZONE = 4; // modules; the QR spec's minimum margin

function QrImage({ text }: { text: string }) {
  const svg = useMemo(() => {
    try {
      const qr = qrcode(0, "M");
      qr.addData(text);
      qr.make();
      const n = qr.getModuleCount();
      let d = "";
      for (let r = 0; r < n; r += 1) {
        for (let c = 0; c < n; c += 1) {
          if (qr.isDark(r, c)) d += `M${c + QUIET_ZONE} ${r + QUIET_ZONE}h1v1h-1z`;
        }
      }
      return { size: n + QUIET_ZONE * 2, d };
    } catch {
      return null;
    }
  }, [text]);
  if (!svg) return <div className="contact-share-note">The QR code could not be drawn. Use the link instead.</div>;
  // Always dark modules on a light background, in both themes: scanners need
  // that contrast. The light quiet zone is part of the image.
  return (
    <svg
      className="contact-share-qr"
      viewBox={`0 0 ${svg.size} ${svg.size}`}
      shapeRendering="crispEdges"
      role="img"
      aria-label="QR code for adding this contact in the MeshCore app"
    >
      <rect width={svg.size} height={svg.size} fill="#ffffff" />
      <path d={svg.d} fill="#000000" />
    </svg>
  );
}

export function ContactShareSection({ name, publicKey, roleKey }: { name: string | null | undefined; publicKey: string | null | undefined; roleKey: string | null | undefined }) {
  const share = useMemo(() => buildContactShare({ name, publicKey, roleKey }), [name, publicKey, roleKey]);
  const [copied, setCopied] = useState(false);
  const linkRef = useRef<HTMLDivElement>(null);

  function selectLink() {
    const el = linkRef.current;
    if (!el) return;
    const range = document.createRange();
    range.selectNodeContents(el);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }

  async function copy(url: string) {
    let done = false;
    try {
      await navigator.clipboard.writeText(url);
      done = true;
    } catch {
      // Clipboard API blocked (for example a non-secure page): fall back to
      // selecting the text and using the browser's copy command.
      selectLink();
      try {
        done = document.execCommand("copy");
      } catch {
        done = false;
      }
    }
    if (!done) selectLink();
    setCopied(done);
    window.setTimeout(() => setCopied(false), 1500);
  }

  return (
    <SectionCard title="Share contact">
      {share.ok ? (
        <div className="contact-share">
          <QrImage text={share.url} />
          <div className="contact-share-text">
            <p className="contact-share-hint">Scan with the MeshCore app to add this contact.</p>
            <div className="contact-share-link" ref={linkRef} onClick={selectLink}>
              {share.url}
            </div>
            <button type="button" className="node-detail-id-copy-btn contact-share-copy" onClick={() => copy(share.url)}>
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
      ) : (
        <div className="contact-share-note">{share.reason}</div>
      )}
    </SectionCard>
  );
}
